import type { PdfRect } from "./pdfBearbeitung";

/**
 * Textzeilen einer PDF-Seite für „Text bearbeiten“ (PO 28.09.2026) — aus den
 * Stücken, die `page.getTextContent()` liefert.
 *
 * PDF.js liefert Text in Stücken (ein Wort, ein Teilwort, eine Zelle …), die
 * `transform` im PDF-Userspace. Wir fügen Stücke zu Zeilen zusammen, die man
 * als Ganzes anklicken und umschreiben kann — so wie Acrobat zeilenweise
 * bearbeitet. Tabellenspalten (große Lücke) bleiben eigene „Zeilen“: man will
 * eine Zelle ändern, nicht die ganze Tabellenzeile.
 *
 * Rein, ohne React/PDF.js — getestet in `zeilen.test.ts`.
 */

/** Der Teil eines PDF.js-`TextItem`, den wir brauchen. */
export interface TextItemLike {
    str: string;
    /** [a, b, c, d, e, f] im PDF-Userspace; e/f = Start der Grundlinie. */
    transform: number[];
    /** Länge entlang der Schreibrichtung (Userspace). */
    width: number;
    height: number;
    fontName: string;
    hasEOL?: boolean;
}

/** Der Teil von `textContent.styles[fontName]`, den wir brauchen. */
export interface FontStyleLike {
    fontFamily: string;
    /** Oberlänge als Anteil der Schriftgröße (positiv). */
    ascent: number;
    /** Unterlänge als Anteil der Schriftgröße (negativ). */
    descent: number;
    vertical?: boolean;
}

export type Drehung = 0 | 90 | 180 | 270;

export interface Zeile {
    seite: number;
    text: string;
    /** Grundlinie Start, Userspace. */
    x: number;
    y: number;
    /** Schriftgröße in pt. */
    groesse: number;
    drehung: Drehung;
    /** Userspace, Ober-/Unterlänge, 0,5 pt Rand. */
    rect: PdfRect;
    schrift: "sans" | "serif" | "mono";
    fett: boolean;
}

/** Rand um die Zeile — Ober-/Unterlängen sicher mit erfassen. */
const RAND = 0.5;
/** Größere Lücke (× Schriftgröße) = neue Zeile, z. B. nächste Tabellenspalte. */
const MAX_LUECKE = 1.5;
/** Kleinere Abweichung der Grundlinie (× Schriftgröße) = dieselbe Zeile. */
const MAX_VERSATZ = 0.3;
/**
 * Ab dieser Lücke (× Schriftgröße) steht zwischen zwei Stücken ein Leerzeichen.
 *
 * Grenzfall bewusst so gelassen: Ein Wortabstand ist in üblichen Schriften
 * 0,25–0,28 × Größe und liegt damit sicher darüber; Unterschneidungen und
 * Sperrungen innerhalb eines Worts liegen meist darunter. Bei Blocksatz mit
 * sehr engen Wortabständen (< 0,2) kann ein Leerzeichen fehlen, bei stark
 * gesperrtem Text eines zu viel — der Nutzer sieht das im Kasten sofort und
 * korrigiert es, bevor er übernimmt. Ein Schwellwert aus der echten
 * Leerzeichenbreite der Schrift wäre genauer, die liefert PDF.js aber nicht.
 */
const LEERZEICHEN_AB = 0.2;

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Richtungsvektor der Schrift — schräger Text (nicht 0/90/180/270) → `null`. */
function drehungAus(t: readonly number[]): Drehung | null {
    const grad = (Math.atan2(t[1], t[0]) * 180) / Math.PI;
    const gerundet = ((Math.round(grad / 90) * 90) % 360 + 360) % 360;
    // Schräg ist, was mehr als 2° von einer Hauptrichtung abweicht.
    let abw = Math.abs(grad - Math.round(grad / 90) * 90);
    if (abw > 180) abw = 360 - abw;
    if (abw > 2) return null;
    return gerundet as Drehung;
}

/** Schreibrichtung als Einheitsvektor im Userspace. */
export const RICHTUNG: Record<Drehung, [number, number]> = { 0: [1, 0], 90: [0, 1], 180: [-1, 0], 270: [0, -1] };

/**
 * Rechteck einer Zeile im Userspace: entlang der Schreibrichtung 0…breite,
 * quer dazu Unterlänge…Oberlänge — gedreht wie die Schrift.
 */
export function zeilenRechteck(
    x: number,
    y: number,
    breite: number,
    groesse: number,
    drehung: Drehung,
    ascent: number,
    descent: number,
): PdfRect {
    const [dx, dy] = RICHTUNG[drehung];
    // Normale (90° gegen den Uhrzeigersinn zur Schreibrichtung) zeigt zur Oberlänge.
    const [nx, ny] = [-dy, dx];
    const xs: number[] = [];
    const ys: number[] = [];
    for (const u of [0, breite]) {
        for (const v of [descent * groesse, ascent * groesse]) {
            xs.push(x + u * dx + v * nx);
            ys.push(y + u * dy + v * ny);
        }
    }
    return [
        r2(Math.min(...xs) - RAND),
        r2(Math.min(...ys) - RAND),
        r2(Math.max(...xs) + RAND),
        r2(Math.max(...ys) + RAND),
    ];
}

function schriftAus(style: FontStyleLike | undefined, name: string | undefined): Zeile["schrift"] {
    // Der echte Name ist verlässlicher als der Fallback-Stil von PDF.js.
    if (name && /courier|mono|consol/i.test(name)) return "mono";
    if (name && /times|georgia|garamond|minion|cambria|book ?antiqua|palatino/i.test(name)) return "serif";
    const familie = style?.fontFamily ?? "";
    if (/mono/i.test(familie)) return "mono";
    if (/serif/i.test(familie) && !/sans/i.test(familie)) return "serif";
    return "sans";
}

const istFett = (name: string | undefined) => !!name && /bold|black|heavy|semibold|demi/i.test(name);

/** Laufende Zeile während des Zusammenfügens. */
interface Offen {
    drehung: Drehung;
    groesse: number;
    fontName: string;
    /** Position entlang/quer zur Schreibrichtung (für den Vergleich). */
    /** Start entlang der Schreibrichtung — vom ersten Stück. */
    entlangStart: number;
    entlangEnde: number;
    /** Grundlinie quer zur Schreibrichtung — vom Stück mit der größten Schrift. */
    quer: number;
    text: string;
}

/**
 * Stücke → Zeilen, sortiert von oben nach unten, dann von links nach rechts.
 * `fontNamen` liefert den echten Schriftnamen (z. B. „ABCDEF+Arial-BoldMT“ aus
 * `page.commonObjs`) — nur daraus lässt sich Fettschrift erkennen.
 */
export function bildeZeilen(
    seite: number,
    items: readonly TextItemLike[],
    styles: Record<string, FontStyleLike>,
    fontNamen?: (fontName: string) => string | undefined,
): Zeile[] {
    const offen: Offen[] = [];
    for (const it of items) {
        if (!it || typeof it.str !== "string" || !it.str) continue;
        const t = it.transform;
        if (!t || t.length < 6) continue;
        const groesse = Math.hypot(t[2], t[3]);
        if (!(groesse > 0)) continue;
        const drehung = drehungAus(t);
        if (drehung === null) continue;
        const [dx, dy] = RICHTUNG[drehung];
        const entlang = t[4] * dx + t[5] * dy;
        const quer = -t[4] * dy + t[5] * dx;
        // Reine Leerraum-Stücke zählen nicht: PDF.js überbrückt damit auch die
        // Lücke zwischen Tabellenspalten („ “ mit der Breite der Lücke) — sie
        // würden die Spalten sonst zu einer Zeile verbinden. Leerzeichen
        // zwischen Wörtern ergeben sich aus der Lücke (siehe LEERZEICHEN_AB).
        if (it.str.trim() === "") continue;
        // Vertikale Schrift (z. B. japanisch von oben nach unten) kann die
        // Zeilen-Logik nicht abbilden — und die Standardschriften auch nicht.
        if (styles[it.fontName]?.vertical) continue;
        const breite = Math.max(0, it.width || 0);

        // Passende laufende Zeile suchen — von der jüngsten rückwärts, damit
        // verschachtelt gelieferte Spalten trotzdem richtig landen.
        let ziel: Offen | undefined;
        for (let i = offen.length - 1; i >= 0; i--) {
            const z = offen[i];
            if (z.drehung !== drehung) continue;
            const g = Math.max(z.groesse, groesse);
            if (Math.abs(z.quer - quer) >= MAX_VERSATZ * g) continue;
            const luecke = entlang - z.entlangEnde;
            if (luecke > MAX_LUECKE * g || luecke < -MAX_VERSATZ * g) continue;
            ziel = z;
            break;
        }

        if (!ziel) {
            offen.push({
                drehung,
                groesse,
                fontName: it.fontName,
                entlangStart: entlang,
                entlangEnde: entlang + breite,
                quer,
                text: it.str,
            });
            continue;
        }
        const luecke = entlang - ziel.entlangEnde;
        const randLeer = /\s$/.test(ziel.text) || /^\s/.test(it.str);
        if (luecke > LEERZEICHEN_AB * Math.max(ziel.groesse, groesse) && !randLeer) ziel.text += " ";
        ziel.text += it.str;
        ziel.entlangEnde = Math.max(ziel.entlangEnde, entlang + breite);
        // Die Zeile übernimmt die größte Schrift samt deren Grundlinie — eine
        // kleine hochgestellte Ziffer am Anfang verschöbe sonst die ganze Zeile.
        if (groesse > ziel.groesse) {
            ziel.groesse = groesse;
            ziel.fontName = it.fontName;
            ziel.quer = quer;
        }
    }

    const zeilen: Zeile[] = offen
        .map((z) => {
            const text = z.text.replace(/\s+$/, "");
            const style = styles[z.fontName];
            const name = fontNamen?.(z.fontName);
            const ascent = style?.ascent && style.ascent > 0 ? style.ascent : 0.8;
            const descent = style?.descent && style.descent < 0 ? style.descent : -0.2;
            // Zurück in Userspace: Start vom ersten Stück, Grundlinie von der größten Schrift.
            const [dx, dy] = RICHTUNG[z.drehung];
            const x = z.entlangStart * dx - z.quer * dy;
            const y = z.entlangStart * dy + z.quer * dx;
            return {
                seite,
                text,
                x: r2(x),
                y: r2(y),
                groesse: r2(z.groesse),
                drehung: z.drehung,
                rect: zeilenRechteck(x, y, z.entlangEnde - z.entlangStart, z.groesse, z.drehung, ascent, descent),
                schrift: schriftAus(style, name),
                fett: istFett(name),
            };
        })
        .filter((z) => z.text !== "");
    return zeilen.sort((a, b) => b.rect[3] - a.rect[3] || a.rect[0] - b.rect[0]);
}

/** Zeile, in deren Rechteck der Punkt liegt; bei Überlappung die kleinste. */
export function zeileAnPunkt(zeilen: readonly Zeile[], x: number, y: number): Zeile | null {
    let beste: Zeile | null = null;
    let besteFlaeche = Infinity;
    for (const z of zeilen) {
        const [x0, y0, x1, y1] = z.rect;
        if (x < x0 || x > x1 || y < y0 || y > y1) continue;
        const flaeche = (x1 - x0) * (y1 - y0);
        if (flaeche < besteFlaeche) {
            beste = z;
            besteFlaeche = flaeche;
        }
    }
    return beste;
}

/**
 * Maße einer Zeile relativ zu ihrer Grundlinie (pt): Oberlänge, Unterlänge und
 * Breite entlang der Schreibrichtung — zurückgerechnet aus `rect`, damit auch
 * ein gespeicherter `PdfTextErsetzen`-Eintrag (ohne Schriftmetrik) den Kasten
 * wieder genau über die alte Zeile legen kann.
 */
export function zeilenMasse(z: { rect: PdfRect; x: number; y: number; drehung: Drehung }): {
    oben: number;
    unten: number;
    breite: number;
} {
    const [x0, y0, x1, y1] = z.rect;
    const w = (a: number) => r2(Math.max(0, a - RAND));
    switch (z.drehung) {
        case 0:
            return { oben: w(y1 - z.y), unten: w(z.y - y0), breite: w(x1 - z.x) };
        case 90:
            return { oben: w(z.x - x0), unten: w(x1 - z.x), breite: w(y1 - z.y) };
        case 180:
            return { oben: w(z.y - y0), unten: w(y1 - z.y), breite: w(z.x - x0) };
        case 270:
            return { oben: w(x1 - z.x), unten: w(z.x - x0), breite: w(z.y - y0) };
    }
}

/** Stabile Id einer Zeile — dieselbe Zeile ergibt beim erneuten Öffnen dieselbe Änderung. */
export const zeilenId = (z: Pick<Zeile, "seite" | "x" | "y">) => `${z.seite}:${r2(z.x)}:${r2(z.y)}`;

/**
 * Bereich, den der Server für eine geänderte Zeile als Text entfernt — Kopie
 * von `redaktionsBand` in `apps/api/src/pdf-bearbeitung/text-schreiben.ts`
 * (dort die Begründung und Messung): quer zur Schrift +0,1…+0,4 × Größe über
 * der Grundlinie, längs die Breite aus `rect`. Hier nur, um vorab zu sagen,
 * welche geänderte Zeile ein Entfernen-Bereich trifft.
 */
export function redaktionsBand(z: { rect: PdfRect; x: number; y: number; groesse: number; drehung: Drehung }): PdfRect {
    const [x0, y0, x1, y1] = z.rect;
    const u = 0.1 * z.groesse;
    const o = 0.4 * z.groesse;
    switch (z.drehung) {
        case 0:
            return [x0, r2(z.y + u), x1, r2(z.y + o)];
        case 90:
            return [r2(z.x - o), y0, r2(z.x - u), y1];
        case 180:
            return [x0, r2(z.y - o), x1, r2(z.y - u)];
        case 270:
            return [r2(z.x + u), y0, r2(z.x + o), y1];
    }
}

/** Schneiden sich zwei Rechtecke (mit Fläche)? */
export const schneidet = (a: PdfRect, b: PdfRect): boolean => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
